import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../../core/services/api.service';
import { ToastService } from 'angular-toastify';
import {
  CityParkingRegisterGroupByIdResponse,
  CityParkingRegisterDetailMemberGroupResponse,
  ConfirmPaymentRegisterPayload,
  MemberVehiclesResponse,
} from '../../../../../../core/models/register-parking';
import { CookieService } from 'ngx-cookie-service';
import { ModalInvoicePdfViewerComponent } from '../../../finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';
import { FinanceAttachmentsComponent } from '../../../../../../shared/components/finance-attachments/finance-attachments.component';

/** parking_fee_type.id ของการชำระแบบโอน */
const TRANSFER_FEE_TYPE_ID = 3;
const MAX_SLIP_BYTES = 5 * 1024 * 1024;

@Component({
  selector: 'app-modal-register-parking-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalInvoicePdfViewerComponent,
    ModalAdminActivityHistoryComponent,
    FinanceAttachmentsComponent,
  ],
  templateUrl: './modal-register-parking-detail.component.html',
  styleUrl: './modal-register-parking-detail.component.css',
})
export class ModalRegisterParkingDetailComponent implements OnInit {
  @Input() id: number = 0;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  data: CityParkingRegisterGroupByIdResponse | null = null;

  status: number = 1;
  registerType: number = 0;
  totalFee: number = 0;
  invoice: string = '';
  name: string = '';
  createDate: string = '';
  description: string = '';
  totalVehicles: number = 0;

  remark: string = '';
  userId: number = 0;

  isLoading: boolean = false;
  isProcessing: boolean = false;
  showConfirmModal: boolean = false;
  showDeleteModal: boolean = false;
  isVisible: boolean = false;
  isConfirmVisible: boolean = false;
  isDeleteVisible: boolean = false;

  showPdfModal = false;
  pdfSrc: Uint8Array | null = null;
  isProcessingPdf = false;

  constructor(
    private apiService: ApiService,
    private toast: ToastService,
    private cookieService: CookieService,
  ) {}

  ngOnInit() {
    setTimeout(() => {
      this.isVisible = true;
    }, 10);

    if (this.id) {
      this.getData();
    }

    const userIdFromSession = sessionStorage.getItem('user_id');
    this.userId =
      parseInt(this.cookieService.get('user_id')) ||
      (userIdFromSession ? JSON.parse(userIdFromSession) : 0);
  }

  private readField(raw: Record<string, unknown>, camel: string, pascal: string): unknown {
    return raw[camel] ?? raw[pascal];
  }

  private normalizeMemberVehicle(raw: Record<string, unknown>): MemberVehiclesResponse {
    return {
      license: String(this.readField(raw, 'license', 'License') ?? ''),
      province: String(this.readField(raw, 'province', 'Province') ?? ''),
      vehicleTypeName: String(this.readField(raw, 'vehicleTypeName', 'VehicleTypeName') ?? ''),
    };
  }

  private normalizeMember(raw: Record<string, unknown>): CityParkingRegisterDetailMemberGroupResponse {
    const vehiclesRaw =
      (this.readField(raw, 'memberVehicles', 'MemberVehicles') as Record<string, unknown>[] | undefined) ?? [];

    return {
      id: Number(this.readField(raw, 'id', 'Id') ?? 0),
      name: String(this.readField(raw, 'name', 'Name') ?? ''),
      uid: String(this.readField(raw, 'uid', 'Uid') ?? ''),
      phone: String(this.readField(raw, 'phone', 'Phone') ?? ''),
      fee: Number(this.readField(raw, 'fee', 'Fee') ?? 0),
      memberStartDate: String(this.readField(raw, 'memberStartDate', 'MemberStartDate') ?? ''),
      memberEndDate: String(this.readField(raw, 'memberEndDate', 'MemberEndDate') ?? ''),
      feeName: String(this.readField(raw, 'feeName', 'FeeName') ?? ''),
      memberVehicles: vehiclesRaw.map((vehicle) => this.normalizeMemberVehicle(vehicle)),
    };
  }

  private normalizeDetailResponse(
    raw: CityParkingRegisterGroupByIdResponse | Record<string, unknown>,
  ): CityParkingRegisterGroupByIdResponse {
    const r = raw as Record<string, unknown>;
    const membersRaw =
      (this.readField(r, 'cityParkingRegisterDetailMemberGroupResponse', 'CityParkingRegisterDetailMemberGroupResponse') as
        | Record<string, unknown>[]
        | undefined) ?? [];

    return {
      id: Number(this.readField(r, 'id', 'Id') ?? 0),
      description: String(this.readField(r, 'description', 'Description') ?? ''),
      cpCustomerId: Number(this.readField(r, 'cpCustomerId', 'CpCustomerId') ?? 0),
      customerName: String(this.readField(r, 'customerName', 'CustomerName') ?? ''),
      receiptId: String(this.readField(r, 'receiptId', 'ReceiptId') ?? ''),
      registerType: Number(this.readField(r, 'registerType', 'RegisterType') ?? 0),
      cpAddressInvoiceId: Number(this.readField(r, 'cpAddressInvoiceId', 'CpAddressInvoiceId') ?? 0),
      status: Number(this.readField(r, 'status', 'Status') ?? 0),
      fullTax: Boolean(this.readField(r, 'fullTax', 'FullTax') ?? false),
      fee: Number(this.readField(r, 'fee', 'Fee') ?? 0),
      siteName: String(this.readField(r, 'siteName', 'SiteName') ?? ''),
      createDate: String(this.readField(r, 'createDate', 'CreateDate') ?? ''),
      feeName: String(this.readField(r, 'feeName', 'FeeName') ?? ''),
      invoiceNo: String(this.readField(r, 'invoiceNo', 'InvoiceNo') ?? ''),
      clientId: Number(this.readField(r, 'clientId', 'ClientId') ?? 0) || null,
      clientName: String(this.readField(r, 'clientName', 'ClientName') ?? ''),
      cityParkingRegisterDetailMemberGroupResponse: membersRaw.map((member) => this.normalizeMember(member)),
      siteId: Number(this.readField(r, 'siteId', 'SiteId') ?? 0),
      feeTypeId: Number(this.readField(r, 'feeTypeId', 'FeeTypeId') ?? 0),
      financeId: Number(this.readField(r, 'financeId', 'FinanceId') ?? 0) || null,
      feeTypeName: String(this.readField(r, 'feeTypeName', 'FeeTypeName') ?? ''),
      bankAccountId: Number(this.readField(r, 'bankAccountId', 'BankAccountId') ?? 0) || null,
      bankName: String(this.readField(r, 'bankName', 'BankName') ?? ''),
    };
  }

  setValueRegisterMemberGroup() {
    if (!this.data) return;

    const members = this.data.cityParkingRegisterDetailMemberGroupResponse ?? [];
    this.name = this.data.description || this.data.siteName || '-';
    this.totalFee = this.calculateGroupTotalFee(members);
    this.invoice = this.data.invoiceNo || '-';
    this.createDate = this.data.createDate;
    this.description = this.data.description;
    this.status = this.data.status;
    this.registerType = this.data.registerType;
    this.totalVehicles = members.reduce(
      (sum, member) => sum + (member.memberVehicles?.length ?? 0),
      0,
    );
  }

  /** ยอดรวมแบบเดียวกับ getRegisterParkingForTable: ราคาต่อคัน × จำนวนรถในแต่ละ member */
  memberLineTotal(member: CityParkingRegisterDetailMemberGroupResponse): number {
    const unitFee = member.fee ?? 0;
    const vehicleCount = member.memberVehicles?.length ?? 0;
    if (vehicleCount <= 0) return 0;
    return unitFee * vehicleCount;
  }

  private calculateGroupTotalFee(members: CityParkingRegisterDetailMemberGroupResponse[]): number {
    return members.reduce((sum, member) => sum + this.memberLineTotal(member), 0);
  }

  getData() {
    this.isLoading = true;
    this.apiService.getCityParkingRegisterMemberGroupById(this.id).subscribe({
      next: (res) => {
        if (Array.isArray(res)) {
          this.toast.error('ไม่พบรายละเอียดรายการ กรุณา restart API แล้วลองใหม่');
          this.data = null;
          this.isLoading = false;
          return;
        }

        this.data = this.normalizeDetailResponse(res);
        this.setValueRegisterMemberGroup();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถดึงข้อมูลได้');
        this.isLoading = false;
      },
    });
  }

  onClose() {
    this.isVisible = false;
    setTimeout(() => {
      this.close.emit();
    }, 300);
  }

  onConfirm() {
    if (!this.id) return;
    if (this.requiresTransferSlip && !this.slipBase64) {
      this.toast.error('กรุณาแนบหลักฐานการโอนเงินก่อนยืนยัน');
      return;
    }
    this.showConfirmModal = true;
    setTimeout(() => {
      this.isConfirmVisible = true;
    }, 10);
  }

  closeConfirmModal() {
    this.isConfirmVisible = false;
    setTimeout(() => {
      this.showConfirmModal = false;
    }, 300);
  }

  confirmAction() {
    this.closeConfirmModal();
    this.isProcessing = true;
    const payload: ConfirmPaymentRegisterPayload = {
      registerGroupId: this.id,
      userId: this.userId,
      remark: this.remark,
      slipBase64: this.slipBase64 || undefined,
      slipFileName: this.slipFileName || undefined,
    };
    this.apiService.confirmCityParkingRegisterMemberGroup(payload).subscribe({
      next: (res) => {
        this.isProcessing = false;
        if (res.isSuccess) {
          this.toast.success('ยืนยันรายการเรียบร้อย');
          this.save.emit();
        } else {
          this.toast.error(res.message || 'เกิดข้อผิดพลาด');
        }
      },
      error: (err) => {
        this.isProcessing = false;
        console.error(err);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      },
    });
  }

  onDelete() {
    if (!this.id) return;

    if (!this.remark || this.remark.trim() === '') {
      this.toast.error('กรุณาระบุหมายเหตุ (Remark) ก่อนลบข้อมูล');
      return;
    }

    this.showDeleteModal = true;
    setTimeout(() => {
      this.isDeleteVisible = true;
    }, 10);
  }

  closeDeleteModal() {
    this.isDeleteVisible = false;
    setTimeout(() => {
      this.showDeleteModal = false;
    }, 300);
  }

  confirmDelete() {
    this.closeDeleteModal();
    this.isProcessing = true;
    const payload: ConfirmPaymentRegisterPayload = {
      registerGroupId: this.id,
      userId: this.userId,
      remark: this.remark,
    };
    this.apiService.cancelCityParkingRegisterMemberGroup(payload).subscribe({
      next: (res) => {
        this.isProcessing = false;
        if (res.isSuccess) {
          this.toast.success('ยกเลิกรายการเรียบร้อย');
          this.save.emit();
          this.onClose();
        } else {
          this.toast.error(res.message || 'เกิดข้อผิดพลาด');
        }
      },
      error: (err) => {
        this.isProcessing = false;
        console.error(err);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      },
    });
  }

  printPdf(): void {
    if (!this.id || this.status !== 2) return;

    this.isProcessingPdf = true;
    this.apiService.getRegisterParkingPdf(this.id).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal = true;
        this.isProcessingPdf = false;
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF รายงานทะเบียนสมาชิกได้');
        this.isProcessingPdf = false;
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal = false;
    this.pdfSrc = null;
  }

  openActivityHistory(): void {
    if (!this.id) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  slipBase64 = '';
  slipFileName = '';

  get isTransferPayment(): boolean {
    if (!this.data) return false;
    return (this.data.bankAccountId ?? 0) > 0 || this.data.feeTypeId === TRANSFER_FEE_TYPE_ID;
  }

  get requiresTransferSlip(): boolean {
    return this.status === 1 && this.isTransferPayment;
  }

  onSlipFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.toast.error('รองรับเฉพาะไฟล์รูปภาพ (PNG / JPG)');
      return;
    }
    if (file.size > MAX_SLIP_BYTES) {
      this.toast.error('ไฟล์ต้องมีขนาดไม่เกิน 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.slipBase64 = String(reader.result ?? '');
      this.slipFileName = file.name;
    };
    reader.readAsDataURL(file);
  }

  clearSlip(): void {
    this.slipBase64 = '';
    this.slipFileName = '';
  }
}
